// src/features/excursions/components/AddExcursionModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Checkbox } from "components/ui/checkbox";
import toast from "react-hot-toast";
import { formatDateToYYYYMMDD } from "utils/dateUtils";
import { CalendarCheck } from "lucide-react";
import { checkHoliday } from "services/holidayService";
import { useClients } from "features/clients/hooks/useClients";
import { useGuias } from "features/guias/hooks/useGuias";
import { getOccupiedCamarotes } from "../hooks/useExcursionsData";
import { useCalendarRules } from "hooks/useCalendarRules";

const vehicleOptions = [
  "Van",
  "Micro",
  "Ônibus",
  "Ônibus + Micro",
  "2 Ônibus",
  "3 Ônibus",
  "4 Ônibus",
];

const camaroteOptions = ["01", "02", "03", "04"];

function AddExcursionModal({ isOpen, onClose, onAddExcursion, defaultDate }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      date: formatDateToYYYYMMDD(defaultDate),
      camaroteRequested: false,
      camaroteNumbers: [],
      status: "Agendado",
      comboIncluded: false,
      isGuide: false,
      organizerName: "",
      organizerPhone: "",
    },
  });

  const { clients, fetchClients, addClient } = useClients();
  const { guias, fetchGuias, addGuia } = useGuias();
  const [filteredClients, setFilteredClients] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [showPhoneSuggestions, setShowPhoneSuggestions] = useState(false);

  const [showGuideNameSuggestions, setShowGuideNameSuggestions] = useState(false);
  const [showGuidePhoneSuggestions, setShowGuidePhoneSuggestions] = useState(false);

  const [occupiedCamarotes, setOccupiedCamarotes] = useState([]);
  const [isCheckingCamarotes, setIsCheckingCamarotes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const camaroteRequested = watch("camaroteRequested");
  const selectedDateString = watch("date");
  const clientNameValue = watch("clientName");
  const clientPhoneValue = watch("clientPhone");
  const isGuide = watch("isGuide");
  const guideNameValue = watch("organizerName");
  const guidePhoneValue = watch("organizerPhone");

  useEffect(() => {
    if (isOpen) {
      fetchClients();
      fetchGuias();
    }
  }, [isOpen, fetchClients, fetchGuias]);

  useEffect(() => {
    let searchTerm = "";
    let shouldSearch = false;

    if (showNameSuggestions && clientNameValue?.length > 1) {
      searchTerm = clientNameValue;
      shouldSearch = true;
    } else if (showPhoneSuggestions && clientPhoneValue?.length > 1) {
      searchTerm = clientPhoneValue;
      shouldSearch = true;
    }

    if (shouldSearch) {
      const lowerTerm = searchTerm.toLowerCase();
      const matches = clients.filter(
        (c) =>
          c.name.toLowerCase().includes(lowerTerm) ||
          c.phone.includes(lowerTerm) ||
          (c.cpf && c.cpf.includes(lowerTerm)),
      );
      setFilteredClients(matches.slice(0, 5));
    } else {
      setFilteredClients([]);
    }
  }, [
    clientNameValue,
    clientPhoneValue,
    showNameSuggestions,
    showPhoneSuggestions,
    clients,
  ]);

  const handleSelectClient = (client) => {
    setValue("clientName", client.name);
    setValue("clientPhone", client.phone);
    setValue("city", client.city || "");
    setShowNameSuggestions(false);
    setShowPhoneSuggestions(false);
  };

  const filteredGuias = useMemo(() => {
    if (!guias || (!showGuideNameSuggestions && !showGuidePhoneSuggestions)) return [];
    
    if (showGuideNameSuggestions) {
      if (!guideNameValue) return guias.slice(0, 5);
      return guias.filter((g) =>
        g.name.toLowerCase().includes(guideNameValue.toLowerCase())
      ).slice(0, 5);
    }
    
    if (showGuidePhoneSuggestions) {
      if (!guidePhoneValue) return guias.slice(0, 5);
      return guias.filter((g) =>
        g.phone.replace(/\D/g, "").includes(guidePhoneValue.replace(/\D/g, ""))
      ).slice(0, 5);
    }
    
    return [];
  }, [guias, guideNameValue, guidePhoneValue, showGuideNameSuggestions, showGuidePhoneSuggestions]);

  const handleSelectGuide = (guia) => {
    setValue("organizerName", guia.name);
    setValue("organizerPhone", guia.phone);
    setShowGuideNameSuggestions(false);
    setShowGuidePhoneSuggestions(false);
  };

  const holidayName = useMemo(() => {
    if (!selectedDateString) return null;
    const [year, month, day] = selectedDateString.split("-").map(Number);
    if (!year || !month || !day) return null;
    const dateObj = new Date(year, month - 1, day);
    return checkHoliday(dateObj);
  }, [selectedDateString]);

  useEffect(() => {
    const fetchCamarotes = async () => {
      if (isOpen && selectedDateString) {
        setIsCheckingCamarotes(true);
        const [year, month, day] = selectedDateString.split("-").map(Number);
        const dateObj = new Date(year, month - 1, day);

        const occupied = await getOccupiedCamarotes(dateObj);
        setOccupiedCamarotes(occupied);
        setIsCheckingCamarotes(false);
      }
    };
    fetchCamarotes();
  }, [selectedDateString, isOpen]);

  useEffect(() => {
    if (isOpen) {
      const formattedDate = formatDateToYYYYMMDD(defaultDate);
      const todayFormatted = formatDateToYYYYMMDD(new Date());
      const initialStatus =
        formattedDate === todayFormatted ? "Confirmado" : "Agendado";

      reset({
        date: formattedDate,
        clientName: "",
        clientPhone: "",
        city: "",
        quantity: "",
        status: initialStatus,
        vehicle: "",
        camaroteRequested: false,
        camaroteNumbers: [],
        comboIncluded: false,
        isGuide: false,
        organizerName: "",
        organizerPhone: "",
      });
      setShowNameSuggestions(false);
      setShowPhoneSuggestions(false);
      setShowGuideNameSuggestions(false);
      setShowGuidePhoneSuggestions(false);
    }
  }, [isOpen, defaultDate, reset]);

  const { checkDateAvailability, isLoading: isLoadingRules } =
    useCalendarRules();

  const onSubmit = async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (!data.clientName || !data.date) {
        toast.error("Data e Nome do Cliente são obrigatórios.");
        return;
      }

      if (isLoadingRules) {
        toast.error("Aguarde, verificando disponibilidade do calendário...");
        return;
      }

      // Convert date string to Date object without timezone issues
      const [year, month, day] = data.date.split("-").map(Number);
      const dateObj = new Date(year, month - 1, day);

      const isDateOpen = checkDateAvailability(dateObj);

      if (!isDateOpen) {
        toast.error(`O Parque está fechado na data selecionada (${data.date}).`);
        return;
      }

      if (
        data.camaroteRequested &&
        (!data.camaroteNumbers || data.camaroteNumbers.length === 0)
      ) {
        toast.error("Selecione pelo menos um número de camarote.");
        return;
      }

      await addClient({
        name: data.clientName,
        phone: data.clientPhone,
        city: data.city,
        source: "excursion_modal",
      });

      if (data.isGuide && data.organizerName) {
        await addGuia({
          name: data.organizerName,
          phone: data.organizerPhone || "",
          source: "excursion_modal",
        });
      }

      await onAddExcursion(data);
      onClose();
    } catch (e) {
      toast.error("Erro ao salvar: " + e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const SuggestionsList = () => (
    <ul className="absolute z-50 w-full bg-white dark:bg-card border border-slate-200 dark:border-border rounded-md shadow-xl mt-1 max-h-48 overflow-y-auto left-0">
      {filteredClients.map((client) => (
        <li
          key={client.id}
          className="px-4 py-3 hover:bg-slate-100 dark:hover:bg-accent cursor-pointer text-sm border-b border-slate-100 dark:border-border last:border-0 transition-colors"
          onClick={() => handleSelectClient(client)}
        >
          <div className="font-medium">{client.name}</div>
          <div className="text-xs text-muted-foreground flex justify-between">
            <span>{client.phone}</span>
            <span>{client.city}</span>
          </div>
        </li>
      ))}
    </ul>
  );

  const GuideSuggestionsList = () => (
    <ul className="absolute z-50 w-full bg-white dark:bg-card border border-slate-200 dark:border-border rounded-md shadow-xl mt-1 max-h-48 overflow-y-auto left-0">
      {filteredGuias.map((guia) => (
        <li
          key={guia.id}
          className="px-4 py-3 hover:bg-slate-100 dark:hover:bg-accent cursor-pointer text-sm border-b border-slate-100 dark:border-border last:border-0 transition-colors"
          onClick={() => handleSelectGuide(guia)}
        >
          <div className="font-medium">{guia.name}</div>
          <div className="text-xs text-muted-foreground flex justify-between">
            <span>{guia.phone}</span>
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="w-full h-[100dvh] max-h-none sm:h-auto sm:max-h-[85vh] sm:max-w-[500px] p-0 border-0 sm:border rounded-none sm:rounded-lg overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-slate-100 dark:border-border shrink-0">
          <DialogTitle>Nova Excursão</DialogTitle>
        </DialogHeader>

        <div className="px-6 py-6 overflow-y-auto flex-1">
          <form id="add-excursion-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8 pb-12">
            {/* -- SEÇÃO: DATA -- */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-primary uppercase tracking-wider flex items-center">
                Data
              </h3>
              <div className="flex flex-col w-fit space-y-1">
                <Label htmlFor="date">Data da Excursão</Label>
                <Input
                  id="date"
                  type="date"
                  {...register("date", { required: true })}
                  className="force-light-datepicker w-full min-w-[160px]"
                />
                {holidayName && (
                  <div className="mt-1 flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-bold px-2 py-1 rounded border border-purple-200 whitespace-nowrap w-full">
                    <CalendarCheck className="h-3 w-3 flex-shrink-0" />
                    {holidayName}
                  </div>
                )}
              </div>
            </div>

            {/* -- SEÇÃO: CLIENTE -- */}
            <div className="space-y-4 pt-2 border-t border-slate-100 dark:border-border">
              <h3 className="text-sm font-semibold text-primary uppercase tracking-wider flex items-center mt-4">
                Cliente
              </h3>
              <div className="space-y-4">
                <div className="space-y-1 relative">
                  <Label htmlFor="clientName">Nome Completo</Label>
                  <Input
                    id="clientName"
                    {...register("clientName", {
                      required: "Nome é obrigatório",
                    })}
                    onFocus={() => {
                      setShowNameSuggestions(true);
                      setShowPhoneSuggestions(false);
                    }}
                    autoComplete="off"
                    placeholder="Buscar cliente..."
                  />
                  {showNameSuggestions && filteredClients.length > 0 && (
                    <SuggestionsList />
                  )}
                  {errors.clientName && (
                    <p className="text-xs text-destructive">
                      {errors.clientName.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1 relative">
                    <Label htmlFor="clientPhone">Telefone</Label>
                    <Input
                      id="clientPhone"
                      {...register("clientPhone")}
                      onFocus={() => {
                        setShowPhoneSuggestions(true);
                        setShowNameSuggestions(false);
                      }}
                      autoComplete="off"
                      placeholder="(99) 99999-9999"
                    />
                    {showPhoneSuggestions && filteredClients.length > 0 && (
                      <SuggestionsList />
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="city">Cidade/Origem</Label>
                    <Input
                      id="city"
                      {...register("city")}
                      placeholder="Ex: Aracaju"
                    />
                  </div>
                </div>

                {/* -- CHECKBOX DE GUIA INTEGRADO -- */}
                <div className="pt-2 border-t mt-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Checkbox
                      id="isGuide"
                      checked={isGuide}
                      onCheckedChange={(val) => setValue("isGuide", val)}
                    />
                    <label
                      htmlFor="isGuide"
                      className="text-sm font-medium leading-none cursor-pointer text-green-700 dark:text-muted-foreground"
                    >
                      É uma reserva de Guia de Turismo?
                    </label>
                  </div>

                  {isGuide && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-1 bg-green-50/50 dark:bg-card/10 p-3 rounded-md border border-green-100 dark:border-border/30">
                      <div className="space-y-1 relative">
                        <Label htmlFor="organizerName" className="text-green-900 dark:text-foreground">Nome do Guia</Label>
                        <Input
                          id="organizerName"
                          {...register("organizerName", { required: isGuide ? "Nome do guia é obrigatório" : false })}
                          onFocus={() => {
                            setShowGuideNameSuggestions(true);
                            setShowGuidePhoneSuggestions(false);
                          }}
                          autoComplete="off"
                          placeholder="Nome do guia..."
                          className="bg-white dark:bg-card"
                        />
                        {showGuideNameSuggestions && filteredGuias.length > 0 && (
                          <GuideSuggestionsList />
                        )}
                        {errors.organizerName && (
                          <p className="text-xs text-destructive">
                            {errors.organizerName.message}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1 relative">
                        <Label htmlFor="organizerPhone" className="text-green-900 dark:text-foreground">Telefone do Guia</Label>
                        <Input
                          id="organizerPhone"
                          {...register("organizerPhone")}
                          onFocus={() => {
                            setShowGuidePhoneSuggestions(true);
                            setShowGuideNameSuggestions(false);
                          }}
                          autoComplete="off"
                          placeholder="(99) 99999-9999"
                          className="bg-white dark:bg-card"
                        />
                        {showGuidePhoneSuggestions && filteredGuias.length > 0 && (
                          <GuideSuggestionsList />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* -- SEÇÃO: DETALHES -- */}
            <div className="space-y-4 pt-2 border-t border-slate-100 dark:border-border">
              <h3 className="text-sm font-semibold text-primary uppercase tracking-wider flex items-center mt-4">
                Detalhes
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="quantity">Quantidade</Label>
                  <Input
                    id="quantity"
                    type="number"
                    {...register("quantity", { valueAsNumber: true })}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="vehicle">Veículo</Label>
                  <Controller
                    name="vehicle"
                    control={control}
                    render={({ field }) => (
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        defaultValue=""
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {vehicleOptions.map((opt) => (
                            <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </div>

              {/* Opções (Pacote e Camarote) */}
              <div className="space-y-4 pt-2">
                <div className="flex items-center space-x-2 border p-3 rounded-md">
                  <Controller
                    name="comboIncluded"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="comboIncluded"
                      />
                    )}
                  />
                  <label
                    htmlFor="comboIncluded"
                    className="text-sm font-medium leading-none cursor-pointer flex-grow"
                  >
                    Combo (Entrada+Almoço)
                  </label>
                </div>

                <div className="border rounded-md p-3 space-y-3">
                  <div className="flex items-center space-x-2">
                    <Controller
                      name="camaroteRequested"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(val) => {
                            field.onChange(val);
                            if (!val) setValue("camaroteNumbers", []);
                          }}
                          id="camaroteRequested"
                        />
                      )}
                    />
                    <label
                      htmlFor="camaroteRequested"
                      className="text-sm font-medium leading-none cursor-pointer flex-grow"
                    >
                      Camarote
                    </label>
                  </div>

                  {camaroteRequested && (
                    <div className="animate-in fade-in slide-in-from-top-1 pt-1">
                      <Label className="text-xs text-muted-foreground mb-2 flex justify-between">
                        Selecione o número:
                        {isCheckingCamarotes && (
                          <span className="italic">Verificando...</span>
                        )}
                      </Label>
                      <Controller
                        name="camaroteNumbers"
                        control={control}
                        render={({ field }) => (
                          <div className="grid grid-cols-4 gap-2">
                            {camaroteOptions.map((num) => {
                              const currentSelection = field.value || [];
                              const isSelected = currentSelection.includes(num);
                              const isOccupied =
                                occupiedCamarotes.includes(num);

                              return (
                                <button
                                  key={num}
                                  type="button"
                                  disabled={isOccupied}
                                  onClick={() => {
                                    if (isSelected) {
                                      field.onChange(
                                        currentSelection.filter(
                                          (n) => n !== num,
                                        ),
                                      );
                                    } else {
                                      if (currentSelection.length < 2) {
                                        field.onChange([
                                          ...currentSelection,
                                          num,
                                        ]);
                                      } else {
                                        toast.error(
                                          "Máximo de 2 camarotes por reserva.",
                                        );
                                      }
                                    }
                                  }}
                                  className={`
                                    min-h-[44px] rounded-md text-sm font-medium transition-colors border
                                    ${
                                      isOccupied
                                        ? "bg-muted text-muted-foreground opacity-50 cursor-not-allowed border-transparent decoration-slice line-through"
                                        : isSelected
                                          ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                          : "bg-background hover:bg-accent hover:text-accent-foreground border-input"
                                    }
                                  `}
                                >
                                  {num}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

          </form>
        </div>
        
        <DialogFooter className="px-6 py-4 border-t border-slate-100 dark:border-border bg-slate-50 dark:bg-card shrink-0">
          <Button form="add-excursion-form" type="submit" size="lg" disabled={isSubmitting} className="w-full sm:w-auto min-h-[44px]">
            {isSubmitting ? "Adicionando..." : "Adicionar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddExcursionModal;
